import React, { useState } from 'react';
import { Alert, Box, IconButton, Collapse, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorageHelper from '../utils/storageHelper';

/**
 * FacebookBrowserWarning Component
 *
 * Displays a warning banner when the user is accessing the site from Facebook's
 * in-app browser, which may have limited functionality (localStorage restrictions).
 */
const FacebookBrowserWarning: React.FC = () => {
    const [dismissed, setDismissed] = useState(false);
    const isFacebookBrowser = StorageHelper.isFacebookBrowser();
    const isUsingFallback = StorageHelper.isUsingFallback();

    // Only show if user is in Facebook browser or storage fallback is active
    if (!isFacebookBrowser && !isUsingFallback) {
        return null;
    }

    // Don't show if dismissed (but never allow dismissing Facebook browser warning)
    if (dismissed && !isFacebookBrowser) {
        return null;
    }

    const handleDismiss = () => {
        // Only allow dismissing if NOT in Facebook browser
        if (!isFacebookBrowser) {
            setDismissed(true);
        }
    };

    return (
        <Collapse in={isFacebookBrowser || !dismissed}>
            <Box sx={{ mb: 2 }}>
                <Alert
                    severity={isFacebookBrowser ? "warning" : "info"}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={handleDismiss}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                    sx={{
                        '& .MuiAlert-message': {
                            width: '100%'
                        }
                    }}
                >
                    {isFacebookBrowser ? (
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                📱 Limited functionality in Facebook's browser
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                You can play the game here, but for the best experience with all features (login, leaderboards, score saving):
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: 'rgba(255, 235, 59, 0.15)',
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'warning.main'
                            }}>
                                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                    Tap the menu
                                </Typography>
                                <OpenInNewIcon sx={{ fontSize: 18, mx: 0.5 }} />
                                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                    and select "Open in Browser"
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic', opacity: 0.8 }}>
                                Safari, Chrome, or your regular browser will give you the full experience
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Browser storage is restricted
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Your game progress may not be saved. For the best experience:
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'warning.main'
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Tap the menu
                                </Typography>
                                <OpenInNewIcon sx={{ fontSize: 18, mx: 0.5 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    and select "Open in Browser"
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Alert>
            </Box>
        </Collapse>
    );
};

export default FacebookBrowserWarning;
